/**
 * PostHog configuration per deployment variant (China web, global web, app WebView).
 * Uses POSTHOG_API_KEY for web; app WebView always uses the global PostHog project.
 */
import config from "@Config/index";
import getCookie from "helpers/getCookie";
import { appOS, isApp } from "hooks/useIsApp";

export type PostHogSource = "amber_china_website" | "amber_global_website" | "amber_app";

export type PostHogRuntimeConfig = {
  apiKey: string;
  host: string;
  source: PostHogSource;
};

const POSTHOG_CHINA_API_HOST = "https://tracking.amberstudent.cn";
const POSTHOG_GLOBAL_API_HOST = "https://posthog.amberstudent.com";

/** Global PostHog project key (app WebView + global web fallback). */
export const POSTHOG_GLOBAL_PROJECT_KEY = "phc_vCbyBuK48hfdALfigRCiaLunweodmY66WAput3j3zNhM";

export const getPostHogConfig = (): PostHogRuntimeConfig => {
  if (isApp()) {
    return {
      apiKey: POSTHOG_GLOBAL_PROJECT_KEY,
      host: POSTHOG_GLOBAL_API_HOST,
      source: "amber_app",
    };
  }

  if (config.IS_CHINA_APP) {
    return {
      apiKey: config.POSTHOG_API_KEY,
      host: POSTHOG_CHINA_API_HOST,
      source: "amber_china_website",
    };
  }

  return {
    apiKey: config.POSTHOG_API_KEY,
    host: POSTHOG_GLOBAL_API_HOST,
    source: "amber_global_website",
  };
};

const DESTINATION_COUNTRY_STORAGE_KEY = "destinationCountry";
const LAST_VISITED_PROPERTY_STORAGE_KEY = "lastVisitedProperty";
const LAST_VISITED_PROPERTY_AT_STORAGE_KEY = "lastVisitedPropertyAt";
const LAST_SEARCHED_REGION_STORAGE_KEY = "lastSearchedRegion";
const LAST_SEARCHED_REGION_AT_STORAGE_KEY = "lastSearchedRegionAt";
const HAS_SHORTLISTED_STORAGE_KEY = "hasShortlisted";
const LEAD_FORM_OPENED_AT_STORAGE_KEY = "leadFormOpenedAt";
const LAST_VISITED_PROPERTY_NAME_STORAGE_KEY = "lastVisitedPropertyName";
const LAST_SEARCHED_REGION_NAME_STORAGE_KEY = "lastSearchedRegionName";
const LAST_APPLIED_FILTER_STORAGE_KEY = "lastAppliedFilter";
const LAST_APPLIED_FILTER_AT_STORAGE_KEY = "lastAppliedFilterAt";
const LAST_ACTIVE_AT_STORAGE_KEY = "lastActiveAt";

/** ISO country from api.country.is (localStorage/cookie populated by fetchCountryData). */
export const getUserCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return readStored("country") || getCookie("userCountry") || "";
};

/** Destination country (search/property region), persisted from appData.countryCode. */
export const setDestinationCountryForPostHog = (countryCode?: string): void => {
  if (typeof window === "undefined" || !countryCode) return;
  writeStored(DESTINATION_COUNTRY_STORAGE_KEY, countryCode);
};

export const getDestinationCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return readStored(DESTINATION_COUNTRY_STORAGE_KEY);
};

/** setItem throws when site data is blocked or full; an analytics write must not take a render down. */
const readStored = (key: string): string => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked or full — the person property is simply not recorded.
  }
};

/** Slug, plus the moment it was last seen — re-visiting the same one still stamps. */
const setTimestampedValue = (key: string, atKey: string, value: string): void => {
  if (typeof window === "undefined" || !value) return;
  if (readStored(key) !== value) writeStored(key, value);
  writeStored(atKey, new Date().toISOString());
};

/** Both or neither, so the slug and its name can never describe different places. */
const setSlugWithName = (
  slugKey: string,
  atKey: string,
  nameKey: string,
  slug?: string,
  name?: string,
): void => {
  if (typeof window === "undefined") return;
  const value = String(slug || "").trim();
  const display = String(name || "").trim();
  if (!value || !display) return;
  setTimestampedValue(slugKey, atKey, value);
  writeStored(nameKey, display);
};

/** Recorded from the event emitter, not each filter component, so they cannot drift. */
export const setLastAppliedFilterForPostHog = (filterAction?: string): void => {
  if (typeof window === "undefined") return;
  const value = String(filterAction || "").trim();
  if (!value) return;
  writeStored(LAST_APPLIED_FILTER_STORAGE_KEY, value);
  // Stamped every time: the question is when they last filtered, not which filter.
  writeStored(LAST_APPLIED_FILTER_AT_STORAGE_KEY, new Date().toISOString());
};

/** Each stamp is a person-row write, and "last active" audiences are cut in hours. */
const LAST_ACTIVE_THROTTLE_MS = 15 * 60 * 1000;

/** Mark this person active, at most once per window. Returns whether it moved. */
export const touchLastActiveForPostHog = (): boolean => {
  if (typeof window === "undefined") return false;
  const now = Date.now();
  const previous = readStored(LAST_ACTIVE_AT_STORAGE_KEY);
  if (previous) {
    const last = new Date(previous).getTime();
    if (!Number.isNaN(last) && now - last < LAST_ACTIVE_THROTTLE_MS) return false;
  }
  writeStored(LAST_ACTIVE_AT_STORAGE_KEY, new Date(now).toISOString());
  return true;
};

export const setLastVisitedPropertyForPostHog = (canonicalName?: string, name?: string): void =>
  setSlugWithName(
    LAST_VISITED_PROPERTY_STORAGE_KEY,
    LAST_VISITED_PROPERTY_AT_STORAGE_KEY,
    LAST_VISITED_PROPERTY_NAME_STORAGE_KEY,
    canonicalName,
    name,
  );

export const setLastSearchedRegionForPostHog = (regionSlug?: string, name?: string): void =>
  setSlugWithName(
    LAST_SEARCHED_REGION_STORAGE_KEY,
    LAST_SEARCHED_REGION_AT_STORAGE_KEY,
    LAST_SEARCHED_REGION_NAME_STORAGE_KEY,
    regionSlug,
    name,
  );

/** Live state, not a sticky "ever" flag: removing a shortlist must stop the notifications. */
export const setHasShortlistedForPostHog = (hasShortlist: boolean): void => {
  if (typeof window === "undefined") return;
  writeStored(HAS_SHORTLISTED_STORAGE_KEY, hasShortlist ? "true" : "false");
};

/** Most recent open, not the first — "opened in the last N days" compares this against now. */
export const setLeadFormOpenedForPostHog = (): void => {
  if (typeof window === "undefined") return;
  writeStored(LEAD_FORM_OPENED_AT_STORAGE_KEY, new Date().toISOString());
};

/** The name already recorded for this slug, for forms whose inventory has none. */
export const getRecordedPropertyNameForPostHog = (canonicalName?: string): string => {
  if (typeof window === "undefined") return "";
  const slug = String(canonicalName || "").trim();
  if (!slug || readStored(LAST_VISITED_PROPERTY_STORAGE_KEY) !== slug) return "";
  return readStored(LAST_VISITED_PROPERTY_NAME_STORAGE_KEY) || "";
};

/** PostHog person property → the localStorage key holding it. Engage reads these names. */
const BEHAVIOURAL_PERSON_PROPERTY_KEYS = {
  last_visited_property: LAST_VISITED_PROPERTY_STORAGE_KEY,
  last_visited_property_at: LAST_VISITED_PROPERTY_AT_STORAGE_KEY,
  last_searched_region: LAST_SEARCHED_REGION_STORAGE_KEY,
  last_searched_region_at: LAST_SEARCHED_REGION_AT_STORAGE_KEY,
  has_shortlisted: HAS_SHORTLISTED_STORAGE_KEY,
  lead_form_opened_at: LEAD_FORM_OPENED_AT_STORAGE_KEY,
  last_visited_property_name: LAST_VISITED_PROPERTY_NAME_STORAGE_KEY,
  last_searched_region_name: LAST_SEARCHED_REGION_NAME_STORAGE_KEY,
  last_applied_filter: LAST_APPLIED_FILTER_STORAGE_KEY,
  last_applied_filter_at: LAST_APPLIED_FILTER_AT_STORAGE_KEY,
  last_active_at: LAST_ACTIVE_AT_STORAGE_KEY,
} as const;

/** Person properties only — Engage reads these; as super properties they would ride every event. */
export const getBehaviouralPersonProperties = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  return Object.entries(BEHAVIOURAL_PERSON_PROPERTY_KEYS).reduce<Record<string, string>>(
    (props, [name, storageKey]) => {
      const value = readStored(storageKey);
      return value ? { ...props, [name]: value } : props;
    },
    {},
  );
};

/** On logout drop only what belongs to the account: a shortlist a signed-out browser cannot see. */
export const clearAccountScopedPostHogProperties = (): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(HAS_SHORTLISTED_STORAGE_KEY);
  } catch {
    // Storage blocked — nothing was stored to begin with.
  }
};

/** Shared event properties for PostHog captures (includes is_app when in app WebView). */
export const getPostHogEventContext = (): Record<string, string | boolean> => {
  const inApp = isApp();
  const userCountry = getUserCountryForPostHog();
  const countryCode = getDestinationCountryForPostHog();

  return {
    ...(inApp && {
      is_app: true,
      ...(appOS ? { app_platform: appOS } : {}),
    }),
    ...(config.IS_CHINA_APP && !inApp && { china_app: true }),
    ...(config.IS_WHITELABEL_APP && { is_whitelabel: true }),
    ...(userCountry && { user_source_country: userCountry }),
    ...(countryCode && { user_destination_country: countryCode }),
  };
};

/** Client-side PostHog is enabled for web and app WebView (not SSR). */
export const isPostHogEnabled = (): boolean => typeof window !== "undefined";

const POSTHOG_CONFIG = getPostHogConfig();

export default POSTHOG_CONFIG;
